import { useState } from 'react';
import { Sheet, field, hint, label } from '../contacts/Sheet.tsx';
import { SEVERITIES, severityWord, useAssignees, useCreateTask, useTaskDepartments, type Department, type Severity } from '../../api/taskQueries.ts';
import { ClientChooser, DueField, Refusal, chip, dueToSend, go, plain, type DueValue } from './Fields.tsx';
import { agencyToday } from './FollowUp.tsx';

/**
 * A new task by hand (ruling 9; source "Manual"). Who may give one to whom is the server's: the departments offered are
 * ONLY its `create` list (GET /tasks/departments: the departments the reader directs, all five for tiers 1-2 and the
 * Executive Case Reviewer), and the people its list for the department chosen (GET /tasks/assignees), with "nobody:
 * the queue" only where it allows; a refusal is shown as it is. The creator sets the priority here; afterwards only a director or
 * tiers 1-2 change it (ruling 12).
 */
export function NewTaskSheet({ client: given, onClose }: { client?: { id: string; name: string; ref: string | null } | null; onClose: () => void }) {
  const departments = useTaskDepartments();
  const create = useCreateTask();
  const today = agencyToday();
  const [client, setClient] = useState<{ id: string; name: string; ref: string | null } | null>(given ?? null);
  const [noClient, setNoClient] = useState(false);
  const [chosen, setDepartment] = useState<Department | ''>('');
  const offered = departments.data?.create ?? [];
  const department: Department | '' = chosen || (offered.length === 1 ? offered[0]! : '');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [to, setTo] = useState('');
  const [due, setDue] = useState<DueValue>({ choice: '1_week', n: '', date: '' });
  const [severity, setSeverity] = useState<Severity>('Normal');
  const [made, setMade] = useState(false);
  const people = useAssignees(department === '' ? null : department, 'assign');
  const dueSpec = dueToSend(due, today);
  const ready = department !== '' && title.trim() !== '' && to !== '' && dueSpec !== null && (client !== null || noClient);

  return (
    <Sheet labelId="new-task" title="New task" onClose={onClose}>
      {made ? (
        <div className="space-y-3">
          <p role="status" className="font-semibold text-neutral-900">The task is made.</p>
          <button type="button" className={go} onClick={onClose}>Close</button>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          create.mutate({
            client_id: noClient ? null : client!.id,
            department: department as Department,
            title: title.trim(),
            ...(detail.trim() ? { detail: detail.trim() } : {}),
            assigned_to_user_id: to === 'queue' ? null : to,
            due: dueSpec!.spec,
            severity,
          }, { onSuccess: () => setMade(true) });
        }}>
          {given ? (
            <p className="text-neutral-900"><span className={label}>Client</span>{given.name} <span className="font-mono text-sm text-neutral-700">{given.ref}</span></p>
          ) : (
            <>
              {!noClient && <ClientChooser chosen={client} onChoose={setClient} />}
              <label className="flex min-h-[44px] items-center gap-2 text-neutral-900">
                <input type="checkbox" className="size-5" checked={noClient} onChange={(e) => { setNoClient(e.target.checked); if (e.target.checked) setClient(null); }} />
                Not about one client
              </label>
            </>
          )}
          {departments.data && offered.length === 0 ? (
            <p role="alert" className="font-semibold text-neutral-900">You may not make a task by hand in any department. A follow-up is made from a task you complete.</p>
          ) : (
            <label className={label}>Department
              <select value={department} onChange={(e) => { setDepartment(e.target.value as Department); setTo(''); }} className={field}>
                {department === '' && <option value="">{departments.isPending ? 'Loading…' : 'Choose…'}</option>}
                {offered.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className={label}>What is to be done
            <input type="text" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={field} />
          </label>
          <label className={label}>Detail (optional)
            <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} className={`${field} py-2`} />
          </label>
          <label className={label}>For
            <select value={to} onChange={(e) => setTo(e.target.value)} className={field} disabled={department === ''}>
              <option value="">{people.isPending && department !== '' ? 'Loading…' : 'Choose…'}</option>
              {people.data?.queue_allowed && <option value="queue">Nobody yet: the department's queue</option>}
              {people.data?.people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </select>
          </label>
          {department !== '' && people.data && people.data.people.length === 0 && !people.data.queue_allowed && (
            <p className={hint}>You may not give a task to anyone in {department}.</p>
          )}
          <DueField value={due} onChange={setDue} today={today} />
          <div role="radiogroup" aria-label="Priority" className="flex flex-wrap gap-2">
            {SEVERITIES.map((s) => (
              <button key={s} type="button" role="radio" aria-checked={severity === s} className={chip(severity === s)} onClick={() => setSeverity(s)}>
                {severityWord(s) ? `${severityWord(s)} (${s})` : s}
              </button>
            ))}
          </div>
          <Refusal error={create.error ?? people.error ?? departments.error} />
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={go} disabled={!ready || create.isPending}>Make the task</button>
            <button type="button" className={plain} onClick={onClose}>Cancel</button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
